// Home (02-DESIGN/03-console/01-the-console.md § the shape): the page after
// sign-in and the rail's first entry. What needs the person — a request
// waiting for their confirmation, a workload that keeps failing or cannot
// start, a person waiting for their first sign-in — each one line and one
// link, at most five, the most urgent first, and *Show all* with the count
// beyond them, which shows the rest in place, under them (§ conventions: the
// moves, page by page); then one tile per thing the role may read, one row;
// then, while the workspace is new, the first steps, struck through when done
// and gone when all are. Home holds no form. A member's Home is the same page
// with the admin's tiles and steps left out: no workload tile, no workload in
// *needs you*, and of the first steps only *connect an app* (hq decision
// 0047). A root's admins read one line while the plan needs them — a trial
// ending, a plan in grace or lapsed — and every admin of a self-hosted
// install past its license's grace reads that, each linking to Settings →
// Plan (02-DESIGN/17-plans/02-features-and-limits.md § the console). Where
// the install bills, a root's admins read the billing line instead of the
// trial's — *Acme's trial ends in 12 days — choose a plan* — and, past due,
// *Acme's payment of €… failed — update your payment method by 15 November*,
// each linking to Settings → Billing (02-DESIGN/18-billing/02-the-billing-page.md
// § Home); nothing while a plan is simply active. Until
// audit and usage graduates
// (research 003), *needs you* draws on what the platform writes today.

import { Box, Check, Plug, Users } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router";

import { tenantPath, type Page } from "../../address/address.ts";
import type { TenantProfile } from "../../platform/profile.ts";
import { usePage } from "../../platform/session.tsx";
import { Button, Dot, PageHead, Pending, plural, since, Tile, when } from "../../ui/components.tsx";
import { Subjects, type AppsListReply, type RowsListReply, type TenantReply } from "../../wire/auth.ts";
import { EscrowAction, escrowSubject, waitingToCollect, type EscrowStatus } from "../../wire/escrow.ts";
import { Status } from "../../wire/guard.ts";
import { listWaiting } from "../approvals/addressed.ts";
import { Role, SERVICE } from "../../wire/identity.ts";
import { listSubject, SERVICE as WORKLOADS, type ListReply } from "../../wire/workloads.ts";
import { describeSubject, principalWords } from "../approvals/words.ts";
import { appsTileNote } from "../apps/state.ts";
import { personName, splitRows } from "../people/rows.ts";
import { useSurface } from "../surface.tsx";
import { plainState, whyNot } from "../workloads/state.ts";
import { useLoad } from "../../ui/components.tsx";
import "../../ui/density.css";
import { Rank, split, urgentFirst, type Need } from "./needs.ts";
import {
  SERVICE as BILLING,
  State as BillingState,
  Subjects as BillingSubjects,
  type ChargesListReply,
  type SubscriptionReply,
} from "../../wire/billing.ts";
import { failedCharge, homeLine as billingLine } from "../settings/billing.ts";
import { homeLine } from "../settings/plan.ts";
import { PLAN_TAB } from "../settings/PlanNotes.tsx";
import { billingPath } from "../settings/receipts.ts";

interface Step {
  label: string;
  done: boolean;
  to: string;
}

export function HomePage({ tenant }: { tenant: TenantProfile }) {
  const s = useSurface();
  const [all, setAll] = useState(false);
  const admin = s.offered("people:rows");
  const approvalsAdmin = s.offered("approvals:admin");
  // Workloads are an admin's page (hq decision 0047): a member's Home reads none.
  const runs = s.offered("workloads:list:any");
  const [apps] = useLoad("home/apps", () =>
    s.bus.call<AppsListReply>(SERVICE, Subjects.appsList(), undefined),
  );
  const [workloads] = useLoad(`home/workloads/${runs ? "admin" : "member"}`, () =>
    runs
      ? s.bus.call<ListReply>(WORKLOADS, listSubject(), undefined)
      : Promise.resolve<ListReply>({ workloads: [] }),
  );
  const [rows] = useLoad(`home/rows/${admin ? "admin" : "member"}`, () =>
    admin
      ? s.bus.call<RowsListReply>(SERVICE, Subjects.rowsList(), undefined)
      : Promise.resolve<RowsListReply>({ rows: [] }),
  );
  // What waits for the person's confirmation; nothing where the workspace has
  // no guardrails service, which is what the platform writes today.
  const [requests] = useLoad(`home/approvals/${approvalsAdmin ? "admin" : "member"}`, () =>
    listWaiting(s.bus, s.principal, approvalsAdmin),
  );

  // An uncollected recovery share counts as an approval does (03-escrow.md § what the console shows).
  const [recovery] = useLoad(`home/recovery/${admin ? "admin" : "member"}`, () =>
    admin
      ? s.bus.call<EscrowStatus>(SERVICE, escrowSubject(EscrowAction.status), undefined)
      : Promise.resolve<EscrowStatus>({ workspace: s.tenant, slots: [] }),
  );

  // The plan, an admin's read (the verb refuses a member).
  const settings = s.offered("settings");
  const [plan] = useLoad(`home/plan/${settings ? "admin" : "member"}`, () =>
    settings
      ? s.bus.call<TenantReply>(SERVICE, Subjects.tenantGet(), undefined)
      : Promise.resolve<TenantReply | undefined>(undefined),
  );

  // Where the install bills, a root's admins read the billing line (an admin's read, a root's only).
  const bills = usePage().platform.billing === true;
  const root = plan.state === "ready" && plan.value !== undefined && plan.value.tenant.root === undefined;
  const [billing] = useLoad(`home/billing/${bills && root ? "root" : "none"}`, async () => {
    if (!bills || !root) {
      return undefined;
    }
    const r = await s.bus.call<SubscriptionReply>(BILLING, BillingSubjects.subscriptionGet(), undefined);
    if (r.subscription?.state !== BillingState.pastDue) {
      return { text: billingLine(s.name, r, undefined), due: false };
    }
    const charges = await s.bus
      .call<ChargesListReply>(BILLING, BillingSubjects.chargesList(), {})
      .then((c) => c.charges)
      .catch(() => []);
    return { text: billingLine(s.name, r, failedCharge(r, charges)), due: true };
  });

  const needs: Need[] = [];
  const trial = plan.state === "ready" && plan.value?.tenant.entitlement?.state === "trial";
  const planSays =
    plan.state === "ready" && plan.value !== undefined && !(bills && trial)
      ? homeLine(plan.value.tenant, s.name)
      : undefined;
  const billingSays = billing.state === "ready" ? billing.value : undefined;
  if (billingSays?.text !== undefined) {
    needs.push({
      key: "billing",
      // A trial ending is news; a payment that failed is trouble.
      tone: billingSays.due ? "bad" : "info",
      text: billingSays.text,
      sub: "Open Settings → Billing to choose a plan or update the payment method.",
      to: billingPath(s.tenant),
      rank: Rank.plan,
    });
  }
  if (planSays !== undefined) {
    needs.push({
      key: "plan",
      // A trial ending is news; a plan past its expiry or a license past its grace is trouble.
      tone:
        plan.state === "ready" &&
        plan.value?.tenant.entitlement?.state === "trial" &&
        plan.value.tenant.license?.state !== "lapsed"
          ? "info"
          : "bad",
      text: planSays,
      sub: "Open Settings → Plan to see what the plan includes and allows.",
      to: tenantPath(s.tenant, "settings"),
      state: { tab: PLAN_TAB },
      rank: Rank.plan,
    });
  }
  if (recovery.state === "ready" && waitingToCollect(recovery.value)) {
    needs.push({
      key: "recovery",
      tone: "info",
      text: "A recovery share is waiting for you to collect",
      sub: `Collect ${s.name}'s share of its recovery keys once, and keep it outside the platform.`,
      // The share is collected on Settings' Zero-trust tab (§ Settings: the Zero-trust tab).
      to: tenantPath(s.tenant, "settings"),
      state: { tab: "zero-trust" },
      rank: Rank.recovery,
    });
  }
  if (requests.state === "ready") {
    for (const r of requests.value.escalations) {
      if (r.status !== Status.open) {
        continue;
      }
      const who = principalWords(r.principal, s.principal);
      needs.push({
        key: `approval/${r.id}`,
        tone: "info",
        text: `A request is waiting for you: ${describeSubject(r.subject)}`,
        sub: `${who === "you" ? "You" : who} asked ${since(r.opened) || when0(r.opened)}; the guardrail ${r.rule} needs confirmation.`,
        to: tenantPath(s.tenant, "approvals", r.id),
        rank: Rank.request,
      });
    }
  }
  if (workloads.state === "ready") {
    for (const w of workloads.value.workloads) {
      const p = plainState(w);
      if (!p.attention) {
        continue;
      }
      const failures = w.placement.failures ?? 0;
      needs.push({
        key: w.id,
        tone: p.tone,
        text: `${w.name} ${p.word === "failing" ? "is failing" : "cannot start"}`,
        // Why it cannot start, as the workload service names it (decision 0034).
        sub:
          whyNot(w.placement) !== ""
            ? `${upper(whyNot(w.placement))}, since ${since(w.state_changed) || when0(w.state_changed)}.`
            : failures > 0
              ? `${plural(failures, "failure")} since ${since(w.state_changed) || when0(w.state_changed)}.`
              : `Since ${since(w.state_changed) || when0(w.state_changed)}.`,
        to: tenantPath(s.tenant, "workloads", w.id),
        rank: Rank.failing,
      });
    }
  }
  const { people, rules } = splitRows(rows.state === "ready" ? rows.value.rows : []);
  if (admin) {
    for (const r of people) {
      if (r.pending === true) {
        needs.push({
          key: `${r.provider}/${r.claim}/${r.value}`,
          tone: "info",
          text: `${personName(r)} is waiting for their first sign-in`,
          sub: `Added ${since(r.written) || when0(r.written)} as ${r.role}. They are in the moment they sign in.`,
          to: tenantPath(s.tenant, "people"),
          rank: Rank.waiting,
        });
      }
    }
  }

  const appList = apps.state === "ready" ? apps.value.apps : [];
  const connected = appList.filter((a) => a.active);
  const workloadList = workloads.state === "ready" ? workloads.value.workloads : [];
  const states = workloadList.map((w) => plainState(w).word);
  const count = (word: string) => states.filter((x) => x === word).length;
  const running = count("running");
  const stopped = count("stopped");
  const failing = count("failing") + count("cannot start");
  const starting = count("starting");
  const members = people.filter((r) => r.pending !== true);
  const waiting = people.length - members.length;
  const admins = members.filter((r) => r.role === Role.admin).length;

  const connect: Step = {
    label: "Connect an app",
    done: appList.length > 0,
    to: tenantPath(s.tenant, "apps"),
  };
  const steps: Step[] = admin
    ? [
        connect,
        {
          label: "Add a colleague",
          done: people.length > 1 || rules.length > 0,
          to: tenantPath(s.tenant, "people"),
        },
        {
          label: `Set up ${s.name}'s own sign-in`,
          done: tenant.provider !== undefined,
          // Settings' Sign-in tab, through the former address that opens it.
          to: `${tenantPath(s.tenant)}/sign-in`,
        },
      ]
    : [connect];
  const loaded =
    apps.state !== "pending" &&
    workloads.state !== "pending" &&
    rows.state !== "pending" &&
    requests.state !== "pending";
  const showSteps = loaded && steps.some((st) => !st.done);

  const { shown, beyond } = split(needs);
  return (
    <>
      <PageHead title={s.name} line="What needs you, and what is running." />

      <section className="section">
        <div className="section-head">
          <h2>Needs you</h2>
          {loaded && needs.length > 0 && <span className="small faint">{plural(needs.length, "thing")}</span>}
        </div>
        {!loaded && needs.length === 0 ? (
          <Pending what="Looking" />
        ) : needs.length === 0 ? (
          <p className="muted">Nothing needs you right now.</p>
        ) : (
          <div className="card slim">
            <NeedList needs={all ? urgentFirst(needs) : shown} />
            {beyond > 0 && (
              <div className="needs-more">
                <Button
                  size="sm"
                  kind="quiet"
                  onClick={() => {
                    setAll(!all);
                  }}
                >
                  {all ? "Show less" : `Show all (${plural(beyond, "more", "more")})`}
                </Button>
              </div>
            )}
          </div>
        )}
      </section>

      <div className="tiles">
        <Tile
          icon={<Plug strokeWidth={1.6} aria-hidden="true" />}
          label="Apps"
          to={tenantPath(s.tenant, "apps")}
          value={
            apps.state === "ready" ? `${connected.length} connected` : apps.state === "refused" ? "—" : "…"
          }
          note={
            apps.state !== "ready"
              ? undefined
              : appsTileNote(connected, s.offered("apps:list:any"), since, when)
          }
        />
        {runs && (
          <Tile
            icon={<Box strokeWidth={1.6} aria-hidden="true" />}
            label="Workloads"
            to={tenantPath(s.tenant, "workloads")}
            attention={failing > 0}
            value={
              workloads.state === "ready" ? `${running} running` : workloads.state === "refused" ? "—" : "…"
            }
            note={
              workloads.state !== "ready"
                ? undefined
                : workloadList.length === 0
                  ? "none yet"
                  : [
                      starting > 0 ? `${starting} starting` : "",
                      stopped > 0 ? `${stopped} stopped` : "",
                      failing > 0 ? `${failing} failing` : "",
                    ]
                      .filter((x) => x !== "")
                      .join(" · ") || "all running"
            }
          />
        )}
        {admin && (
          <Tile
            icon={<Users strokeWidth={1.6} aria-hidden="true" />}
            label="People"
            to={tenantPath(s.tenant, "people")}
            value={
              rows.state === "ready" ? plural(members.length, "member") : rows.state === "refused" ? "—" : "…"
            }
            note={
              rows.state !== "ready"
                ? undefined
                : [waiting > 0 ? `${waiting} waiting` : "", plural(admins, "admin")]
                    .filter((x) => x !== "")
                    .join(" · ")
            }
          />
        )}
      </div>

      {showSteps && (
        <section className="section">
          <div className="section-head">
            <h2>First steps</h2>
            <span className="small faint">
              {steps.filter((st) => st.done).length} of {steps.length} done
            </span>
          </div>
          <div className="card slim">
            <div className="list">
              {steps.map((st) => (
                <StepRow key={st.label} step={st} />
              ))}
            </div>
          </div>
        </section>
      )}
    </>
  );
}

/** NeedList is the lines of *Needs you*, each one line and one link. */
function NeedList({ needs }: { needs: readonly Need[] }) {
  return (
    <div className="list">
      {needs.map((n) => (
        <Link className="li" to={n.to} state={n.state} key={n.key}>
          <Dot tone={n.tone} />
          <span className="grow">
            <span className="cell-main">{n.text}</span>
            <span className="cell-sub">{n.sub}</span>
          </span>
          <span className="small faint">Open</span>
        </Link>
      ))}
    </div>
  );
}

function StepRow({ step }: { step: Step }) {
  if (step.done) {
    return (
      <div className="li">
        <span className="check done" aria-hidden="true">
          <Check strokeWidth={2.4} />
        </span>
        <span className="grow done-text">{step.label}</span>
      </div>
    );
  }
  return (
    <Link className="li" to={step.to}>
      <span className="check" aria-hidden="true" />
      <span className="grow text-link">{step.label}</span>
    </Link>
  );
}

/** when0 falls back to the instant's date when it is not recent enough for "ago". */
function when0(iso: string): string {
  return iso === "" ? "a while" : iso.slice(0, 10);
}

export type { Page };

/** upper starts a sentence with a capital. */
function upper(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
